import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { Helmet } from 'react-helmet';
import axios from 'axios';
import API_URL from '../api/apiUrl';
import { examNamesByCategory } from '../components/ExamArrays';
import './BookExamPage.css';

// Map short exam codes to full category names
const EXAM_CATEGORY_MAP = {
    'DHA': 'DHA - Dubai Health Authority',
    'SCFHS': 'SCFHS - Saudi Commission for Health Specialties',
    'DHP': 'DHP - Department of Healthcare Professions Qatar',
    'MOHKW': 'MOHKW - Ministry of Health Kuwait',
    'NHRA': 'NHRA - National Health Regulatory Authority Bahrain',
};

const EXAM_INFO = {
    'DHA': { fullName: 'Dubai Health Authority', price: 300 },
    'SCFHS': { fullName: 'Saudi Commission for Health Specialties', price: 300 },
    'DHP': { fullName: 'Department of Healthcare Professions - Qatar', price: 300 },
    'MOHKW': { fullName: 'Ministry of Health - Kuwait', price: 300 },
    'NHRA': { fullName: 'National Health Regulatory Authority - Bahrain', price: 300 },
};

const BookExamPage = () => {
    const { examCategory } = useParams();
    const navigate = useNavigate();

    const [currentStep, setCurrentStep] = useState(1);
    const [error, setError] = useState('');

    // Step 1: Exam Selection
    const [specialties, setSpecialties] = useState([]);
    const [examNames, setExamNames] = useState([]);
    const [selectedSpecialty, setSelectedSpecialty] = useState('');
    const [selectedExamName, setSelectedExamName] = useState('');

    // Step 2: Date Selection
    const [availableDates, setAvailableDates] = useState([]);
    const [selectedDate, setSelectedDate] = useState('');
    const [selectedTime, setSelectedTime] = useState('');
    const [selectedTimeDisplay, setSelectedTimeDisplay] = useState('');
    const [selectedDateId, setSelectedDateId] = useState(null);
    const [loadingDates, setLoadingDates] = useState(false);
    const [selectedDateTemp, setSelectedDateTemp] = useState(null);

    const examInfo = EXAM_INFO[examCategory] || { fullName: examCategory, price: 320 };
    const fullCategoryName = EXAM_CATEGORY_MAP[examCategory] || examCategory;

    // Load specialties when component mounts
    useEffect(() => {
        if (fullCategoryName && examNamesByCategory[fullCategoryName]) {
            const availableSpecialties = Object.keys(examNamesByCategory[fullCategoryName]);
            setSpecialties(availableSpecialties);
        }
    }, [fullCategoryName]);

    // Load exam names when specialty changes
    useEffect(() => {
        if (selectedSpecialty && examNamesByCategory[fullCategoryName]) {
            const names = examNamesByCategory[fullCategoryName][selectedSpecialty] || [];
            setExamNames(names);
            setSelectedExamName('');
        } else {
            setExamNames([]);
        }
    }, [selectedSpecialty, fullCategoryName]);

    // Fetch available dates when exam is selected
    useEffect(() => {
        const fetchAvailableDates = async () => {
            if (!selectedExamName) return;

            setLoadingDates(true);
            setError('');

            try {
                // Reset selections when loading new dates
                setSelectedDateTemp(null);
                setSelectedDate('');
                setSelectedTime('');
                setSelectedTimeDisplay('');
                setSelectedDateId(null);

                const response = await axios.get(`${API_URL}api/external-exam/available-dates/${examCategory}`);
                if (response.data.success) {
                    setAvailableDates(response.data.dates || []);
                } else {
                    setError('Failed to load available dates');
                }
            } catch (err) {
                console.error('Error fetching available dates:', err);
                setError('Failed to load available dates. Please try again.');
            } finally {
                setLoadingDates(false);
            }
        };

        if (currentStep === 2) {
            fetchAvailableDates();
        }
    }, [currentStep, examCategory, selectedExamName]);

    const handleSpecialtyChange = (e) => {
        setSelectedSpecialty(e.target.value);
    };

    const handleExamNameChange = (e) => {
        setSelectedExamName(e.target.value);
    };

    const handleDateSelect = (dateInfo) => {
        console.log('Selected date info:', dateInfo);
        console.log('Selected available_date:', dateInfo.available_date);
        // Ensure we extract just the date portion (YYYY-MM-DD) to avoid timezone issues
        let dateValue = dateInfo.available_date;
        if (dateValue && dateValue.includes('T')) {
            dateValue = dateValue.split('T')[0];
        }
        setSelectedDate(dateValue);
        setSelectedTime(dateInfo.start_time);
        setSelectedTimeDisplay(dateInfo.start_time_display || '');
        setSelectedDateId(dateInfo.id);
    };

    const handleNextStep = () => {
        if (currentStep === 1) {
            if (!selectedExamName) {
                setError('Please select an exam name');
                return;
            }
            setError('');
            setCurrentStep(2);
        } else if (currentStep === 2) {
            if (!selectedDateId) {
                setError('Please select an exam date and time');
                return;
            }
            setError('');
            // Navigate to registration form with all selected data
            console.log('Navigating to /external-register with examDate:', selectedDate);
            navigate('/external-register', {
                state: {
                    examCategory: examCategory,
                    examCategoryFull: fullCategoryName,
                    examSpecialty: selectedSpecialty,
                    examName: selectedExamName,
                    examDate: selectedDate,
                    examDateId: selectedDateId,
                    examStartTime: selectedTime,
                    examPrice: examInfo.price
                }
            });
        }
    };

    const handlePrevStep = () => {
        if (currentStep > 1) {
            setCurrentStep(currentStep - 1);
            setError('');
        }
    };

    // Helper function to parse date string as local time (not UTC)
    // Prevents timezone shift when date string like "2026-02-02" is interpreted as UTC
    const parseLocalDate = (dateStr) => {
        if (!dateStr) return new Date();
        // If it's already a full ISO string with time, extract just the date part
        const datePart = dateStr.includes('T') ? dateStr.split('T')[0] : dateStr;
        // Append T12:00:00 to ensure it's treated as local noon (safe from any timezone offset)
        return new Date(datePart + 'T12:00:00');
    };

    const formatDate = (dateStr) => {
        const date = parseLocalDate(dateStr);
        return date.toLocaleDateString('en-US', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    return (
        <>
            <Helmet>
                <title>Book {examCategory} Exam | Talent Track</title>
                <meta name="description" content={`Book your ${examInfo.fullName} exam with Talent Track. Select your specialty and preferred date.`} />
            </Helmet>

            <div className="book-exam-page">
                <Container>
                    {/* Header */}
                    <div className="book-exam-header text-center mb-5">
                        <div className="exam-badge mb-3">
                            <span className="badge-icon">{examCategory}</span>
                        </div>
                        <h1 className="page-title">Book Your {examCategory} Exam</h1>
                        <p className="page-subtitle">{examInfo.fullName}</p>
                        <div className="exam-price-display">
                            <span className="price-label">Exam Fee:</span>
                            <span className="price-amount">${examInfo.price}</span>
                        </div>
                    </div>

                    {/* Progress Indicator */}
                    <div className="step-indicator mb-5">
                        <div className={`step ${currentStep >= 1 ? 'active' : ''} ${currentStep > 1 ? 'completed' : ''}`}>
                            <div className="step-number">1</div>
                            <div className="step-label">Select Exam</div>
                        </div>
                        <div className="step-line"></div>
                        <div className={`step ${currentStep >= 2 ? 'active' : ''} ${currentStep > 2 ? 'completed' : ''}`}>
                            <div className="step-number">2</div>
                            <div className="step-label">Choose Date</div>
                        </div>
                        <div className="step-line"></div>
                        <div className={`step ${currentStep >= 3 ? 'active' : ''}`}>
                            <div className="step-number">3</div>
                            <div className="step-label">Register</div>
                        </div>
                    </div>

                    {error && (
                        <Alert variant="danger" className="mb-4" onClose={() => setError('')} dismissible>
                            {error}
                        </Alert>
                    )}

                    {/* Step Content */}
                    <Card className="book-exam-card shadow-lg">
                        <Card.Body className="p-4 p-md-5">
                            {currentStep === 1 && (
                                <div className="step-content">
                                    <h3 className="step-title mb-4">Select Your Exam</h3>

                                    <Row>
                                        <Col md={6} className="mb-4">
                                            <Form.Group>
                                                <Form.Label className="form-label-custom">
                                                    <i className="bi bi-list-ul me-2"></i>
                                                    Specialty / Category
                                                </Form.Label>
                                                <Form.Select
                                                    value={selectedSpecialty}
                                                    onChange={handleSpecialtyChange}
                                                    className="form-select-custom"
                                                >
                                                    <option value="">-- Select Specialty --</option>
                                                    {specialties.map((specialty) => (
                                                        <option key={specialty} value={specialty}>
                                                            {specialty}
                                                        </option>
                                                    ))}
                                                </Form.Select>
                                            </Form.Group>
                                        </Col>

                                        <Col md={6} className="mb-4">
                                            <Form.Group>
                                                <Form.Label className="form-label-custom">
                                                    <i className="bi bi-file-earmark-text me-2"></i>
                                                    Exam Name
                                                </Form.Label>
                                                <Form.Select
                                                    value={selectedExamName}
                                                    onChange={handleExamNameChange}
                                                    className="form-select-custom"
                                                    disabled={!selectedSpecialty}
                                                >
                                                    <option value="">-- Select Exam Name --</option>
                                                    {examNames.map((name) => (
                                                        <option key={name} value={name}>
                                                            {name}
                                                        </option>
                                                    ))}
                                                </Form.Select>
                                            </Form.Group>
                                        </Col>
                                    </Row>

                                    {selectedExamName && (
                                        <div className="selection-summary mt-4 p-3 rounded">
                                            <h5 className="mb-2">Your Selection:</h5>
                                            <p className="mb-1"><strong>Category:</strong> {examCategory}</p>
                                            <p className="mb-1"><strong>Specialty:</strong> {selectedSpecialty}</p>
                                            <p className="mb-0"><strong>Exam:</strong> {selectedExamName}</p>
                                        </div>
                                    )}
                                </div>
                            )}

                            {currentStep === 2 && (
                                <div className="step-content">
                                    <h3 className="step-title mb-4">
                                        {!selectedDateTemp ? 'Select Exam Date' : 'Select Exam Time'}
                                    </h3>

                                    <div className="mb-4 p-3 bg-light rounded">
                                        <div className="d-flex flex-column mb-2">
                                            <p className="mb-1"><strong>Selected Exam:</strong> {selectedExamName}</p>
                                            <p className="mb-0"><strong>Specialty:</strong> {selectedSpecialty}</p>
                                        </div>
                                        {selectedDateTemp && (
                                            <Button
                                                variant="outline-primary"
                                                size="sm"
                                                onClick={() => {
                                                    setSelectedDateTemp(null);
                                                    setSelectedDateId(null);
                                                    setSelectedDate('');
                                                    setSelectedTime('');
                                                    setSelectedTimeDisplay('');
                                                }}
                                            >
                                                Change Date
                                            </Button>
                                        )}
                                    </div>

                                    {loadingDates ? (
                                        <div className="text-center py-5">
                                            <Spinner animation="border" variant="primary" />
                                            <p className="mt-3">Loading available dates...</p>
                                        </div>
                                    ) : availableDates.length === 0 ? (
                                        <Alert variant="warning">
                                            <i className="bi bi-exclamation-triangle me-2"></i>
                                            No available dates found for this exam category. Please contact support or try again later.
                                        </Alert>
                                    ) : !selectedDateTemp ? (
                                        /* Show Dates First */
                                        (() => {
                                            // Calculate minimum allowed date (18 days from today)
                                            const today = new Date();
                                            today.setHours(0, 0, 0, 0);
                                            const minDate = new Date(today);
                                            minDate.setDate(minDate.getDate() + 18);

                                            // Get unique dates, filter by slots > 0 and >= 18 days from now
                                            const validDates = Array.from(new Set(availableDates.map(d => d.available_date.split('T')[0])))
                                                .filter(dateStr => {
                                                    const dateSlots = availableDates.filter(d => d.available_date.startsWith(dateStr));
                                                    const totalAvailable = dateSlots.reduce((acc, curr) => acc + (curr.slots_available - (curr.slots_booked || 0)), 0);

                                                    // Check if date is at least 18 days from today
                                                    const examDate = parseLocalDate(dateStr);
                                                    const daysDiff = Math.ceil((examDate - today) / (1000 * 60 * 60 * 24));

                                                    // Only show if slots > 0 AND date is >= 18 days from now
                                                    return totalAvailable > 0 && daysDiff >= 18;
                                                })
                                                .sort((a, b) => new Date(a) - new Date(b));

                                            if (validDates.length === 0) {
                                                return (
                                                    <Alert variant="warning">
                                                        <i className="bi bi-exclamation-triangle me-2"></i>
                                                        No available dates found. All dates are either fully booked or too close to the current date (must be at least 18 days in advance).
                                                    </Alert>
                                                );
                                            }

                                            return (
                                                <div className="date-grid">
                                                    {validDates.map((dateStr) => {
                                                        const dateSlots = availableDates.filter(d => d.available_date.startsWith(dateStr));
                                                        const totalAvailable = dateSlots.reduce((acc, curr) => acc + (curr.slots_available - (curr.slots_booked || 0)), 0);

                                                        return (
                                                            <div
                                                                key={dateStr}
                                                                className={`date-card ${selectedDate === dateStr ? 'selected' : ''}`}
                                                                onClick={() => setSelectedDateTemp(dateStr)}
                                                            >
                                                                <div className="date-day">
                                                                    {parseLocalDate(dateStr).toLocaleDateString('en-US', { weekday: 'short' })}
                                                                </div>
                                                                <div className="date-number">
                                                                    {parseLocalDate(dateStr).getDate()}
                                                                </div>
                                                                <div className="date-month">
                                                                    {parseLocalDate(dateStr).toLocaleDateString('en-US', { month: 'short' })}
                                                                </div>
                                                                <div className="date-slots">
                                                                    {totalAvailable} slots available
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            );
                                        })()
                                    ) : (
                                        /* Show Times for Selected Date */
                                        <div className="time-selection-area">
                                            <h5 className="mb-3">Available Times for {formatDate(selectedDateTemp)}:</h5>
                                            <div className="date-grid">
                                                {availableDates
                                                    .filter(d => d.available_date.startsWith(selectedDateTemp))
                                                    .map((timeSlot) => {
                                                        const isAvailable = timeSlot.slots_available > (timeSlot.slots_booked || 0);
                                                        return (
                                                            <div
                                                                key={timeSlot.id}
                                                                className={`date-card time-card ${selectedDateId === timeSlot.id ? 'selected' : ''} ${!isAvailable ? 'disabled' : ''}`}
                                                                onClick={() => {
                                                                    if (isAvailable) {
                                                                        handleDateSelect(timeSlot);
                                                                    }
                                                                }}
                                                            >
                                                                <div className="date-time text-primary fw-bold my-2">
                                                                    <i className="bi bi-clock me-1"></i>
                                                                    {timeSlot.start_time_display}
                                                                </div>
                                                                <div className="date-slots">
                                                                    {timeSlot.slots_available - (timeSlot.slots_booked || 0)} left
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                            </div>
                                        </div>
                                    )}

                                    {selectedDate && selectedTimeDisplay && (
                                        <div className="selection-summary mt-4 p-3 rounded">
                                            <h5 className="mb-2">Confirmed Selection:</h5>
                                            <p className="mb-1"><i className="bi bi-calendar-check me-2"></i>{formatDate(selectedDate)}</p>
                                            <p className="mb-0"><i className="bi bi-clock me-2"></i>{selectedTimeDisplay}</p>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Navigation Buttons */}
                            <div className="step-navigation mt-5 d-flex justify-content-between">
                                <Button
                                    variant="outline-secondary"
                                    onClick={currentStep === 1 ? () => navigate('/') : handlePrevStep}
                                    className="btn-nav"
                                >
                                    <i className="bi bi-arrow-left me-2"></i>
                                    {currentStep === 1 ? 'Back to Home' : 'Previous'}
                                </Button>

                                <Button
                                    variant="primary"
                                    onClick={handleNextStep}
                                    className="btn-nav btn-next"
                                    disabled={(currentStep === 1 && !selectedExamName) || (currentStep === 2 && !selectedDate)}
                                >
                                    {currentStep === 2 ? 'Continue to Registration' : 'Next'}
                                    <i className="bi bi-arrow-right ms-2"></i>
                                </Button>
                            </div>
                        </Card.Body>
                    </Card>
                </Container>
            </div>
        </>
    );
};

export default BookExamPage;
